// src/HomePage/Game/Werewolf/WerewolfLobby.js
import React, { useState } from 'react';
import { useWerewolf, getAvatarIcon } from './WerewolfContext';
import './WerewolfLobby.css';

const WerewolfLobby = () => {
  const { rooms, loading, error, createRoom, joinRoom } = useWerewolf();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [pinInput, setPinInput] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPassModal, setShowPassModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [passInput, setPassInput] = useState('');

  const [newRoomName, setNewRoomName] = useState('');
  const [newMaxPlayers, setNewMaxPlayers] = useState(10);
  const [newPassword, setNewPassword] = useState('');

  const filteredRooms = rooms.filter(room => {
    const hostFullName = `${room.hostHolyName || ''} ${room.hostName || ''}`.toLowerCase();
    const matchName = (room.roomName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                      hostFullName.includes(searchTerm.toLowerCase()) ||
                      (room.roomCode || '').includes(searchTerm);

    if (!matchName) return false;

    if (filterStatus === 'waiting') return room.status === 'WAITING';
    if (filterStatus === 'unlocked') return !room.isLocked;

    return true;
  });

  const handleFormCreate = async (e) => {
    e.preventDefault();
    const result = await createRoom({
      roomName: newRoomName,
      maxPlayers: newMaxPlayers,
      password: newPassword
    });

    if (result.success) {
      setShowCreateModal(false);
      setNewRoomName('');
      setNewPassword('');
    }
  };

  const handleRoomClick = (room) => {
    if (room.isLocked) {
      setSelectedRoom(room);
      setPassInput('');
      setShowPassModal(true);
    } else {
      joinRoom(room.roomId);
    }
  };

  const handlePassSubmit = async (e) => {
    e.preventDefault();
    if (!selectedRoom) return;

    const res = await joinRoom(selectedRoom.roomId, passInput);
    if (res.success) {
      setShowPassModal(false);
      setSelectedRoom(null);
    } else {
      alert(res.message);
    }
  };

  const handleQuickJoinPin = async (e) => {
    e.preventDefault();
    const cleanPin = pinInput.trim();
    if (!cleanPin) return;

    const target = rooms.find(r => r.roomCode === cleanPin);
    if (!target) {
      alert('Không tìm thấy phòng với mã PIN này!');
      return;
    }

    handleRoomClick(target);
  };

  return (
    <div className="ww-lobby-container">
      <div className="ww-lobby-topbar">
        <div className="ww-quick-join-box">
          <form onSubmit={handleQuickJoinPin} className="ww-pin-form">
            <input
              type="text"
              placeholder="Nhập mã PIN 6 số..."
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value)}
              maxLength={6}
              className="ww-pin-input"
            />
            <button type="submit" className="ww-btn primary">Vào Nhanh 🚀</button>
          </form>
        </div>

        <button className="ww-btn create-btn" onClick={() => setShowCreateModal(true)}>
          ➕ Tạo Phòng Mới
        </button>
      </div>

      <div className="ww-filter-bar">
        <input
          type="text"
          placeholder="🔍 Tìm tên phòng, tên chủ phòng hoặc mã PIN..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="ww-search-input"
        />

        <div className="ww-filter-tabs">
          <button
            className={`ww-filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
            onClick={() => setFilterStatus('all')}
          >
            Tất cả ({rooms.length})
          </button>
          <button
            className={`ww-filter-btn ${filterStatus === 'waiting' ? 'active' : ''}`}
            onClick={() => setFilterStatus('waiting')}
          >
            ⏳ Đang chờ
          </button>
          <button
            className={`ww-filter-btn ${filterStatus === 'unlocked' ? 'active' : ''}`}
            onClick={() => setFilterStatus('unlocked')}
          >
            🔓 Không khóa
          </button>
        </div>
      </div>

      {error && <div className="ww-error-toast">{error}</div>}

      {loading ? (
        <div className="ww-loading-spinner">Đang tải danh sách phòng... 🐺</div>
      ) : filteredRooms.length === 0 ? (
        <div className="ww-empty-state">
          <p>Chưa có phòng chơi nào phù hợp.</p>
          <button className="ww-btn primary" onClick={() => setShowCreateModal(true)}>
            Tạo phòng ngay để làm Chủ Phòng 🎲
          </button>
        </div>
      ) : (
        <div className="ww-room-grid">
          {filteredRooms.map((room) => {
            const isFull = room.currentPlayersCount >= room.maxPlayers;
            const isPlaying = room.status === 'PLAYING';
            const hostAvatar = getAvatarIcon(room.hostUid);

            return (
              <div
                key={room.roomId}
                className={`ww-room-card ${isPlaying ? 'playing' : ''} ${isFull ? 'full' : ''}`}
                onClick={() => !isPlaying && handleRoomClick(room)}
              >
                <div className="ww-card-header">
                  <span className="ww-room-code">#{room.roomCode}</span>
                  <div className="ww-badge-group">
                    {room.isLocked && <span className="ww-badge lock">🔒 Khóa</span>}
                    {isPlaying ? (
                      <span className="ww-badge status playing">🎮 Đang chơi</span>
                    ) : isFull ? (
                      <span className="ww-badge status full">👥 Đầy</span>
                    ) : (
                      <span className="ww-badge status waiting">⏳ Đang chờ</span>
                    )}
                  </div>
                </div>

                <h4 className="ww-room-name">{room.roomName}</h4>

                <div className="ww-host-info">
                  <span className="ww-host-label">Chủ phòng:</span>
                  <span className="ww-host-name">
                    {hostAvatar} {room.hostRole === 'catechist' && <span className="ww-glv-tag">🛡️ GLV</span>}
                    <b>{room.hostHolyName} {room.hostName}</b>
                  </span>
                </div>

                <div className="ww-card-footer">
                  <div className="ww-player-progress">
                    <div
                      className="ww-progress-bar"
                      style={{ width: `${(room.currentPlayersCount / room.maxPlayers) * 100}%` }}
                    ></div>
                    <span className="ww-player-count">
                      👥 {room.currentPlayersCount}/{room.maxPlayers}
                    </span>
                  </div>

                  <button
                    className={`ww-join-btn ${isPlaying || isFull ? 'disabled' : ''}`}
                    disabled={isPlaying || isFull}
                  >
                    {isPlaying ? 'Đang chơi' : isFull ? 'Hết chỗ' : 'Vào Phòng ➔'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreateModal && (
        <div className="ww-modal-overlay">
          <div className="ww-modal-card">
            <h3>🎲 Tạo Phòng Ma Sói Mới</h3>
            <form onSubmit={handleFormCreate} className="ww-modal-form">
              <label>Tên Phòng:</label>
              <input
                type="text"
                placeholder="VD: Đêm Huyền Bí - Khối Thêm Sức"
                value={newRoomName}
                onChange={(e) => setNewRoomName(e.target.value)}
                className="ww-modal-input"
              />

              <label>Số người chơi tối đa (6 - 15 người):</label>
              <select
                value={newMaxPlayers}
                onChange={(e) => setNewMaxPlayers(e.target.value)}
                className="ww-modal-input"
              >
                {[6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(num => (
                  <option key={num} value={num}>{num} Người chơi</option>
                ))}
              </select>

              <label>Mật khẩu (Bỏ trống nếu muốn mở công khai):</label>
              <input
                type="password"
                placeholder="Nhập mật khẩu (Không bắt buộc)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="ww-modal-input"
              />

              <div className="ww-modal-actions">
                <button type="button" className="ww-btn danger" onClick={() => setShowCreateModal(false)}>
                  Hủy
                </button>
                <button type="submit" className="ww-btn success">
                  Tạo & Vào Phòng 🚀
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPassModal && selectedRoom && (
        <div className="ww-modal-overlay">
          <div className="ww-modal-card">
            <h3>🔒 Phòng #{selectedRoom.roomCode} Đã Khóa</h3>
            <p>Vui lòng nhập mật khẩu phòng để tham gia:</p>
            <form onSubmit={handlePassSubmit} className="ww-modal-form">
              <input
                type="password"
                placeholder="Nhập mật khẩu phòng..."
                value={passInput}
                onChange={(e) => setPassInput(e.target.value)}
                autoFocus
                className="ww-modal-input"
              />
              <div className="ww-modal-actions">
                <button
                  type="button"
                  className="ww-btn danger"
                  onClick={() => { setShowPassModal(false); setSelectedRoom(null); }}
                >
                  Hủy
                </button>
                <button type="submit" className="ww-btn success">Xác Nhận ➔</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WerewolfLobby;